import { useCallback, useEffect, useState } from 'react';

import { Check, Loader2, X as XIcon } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';

import { notify } from '@/components/toast';
import type { BatchJob } from '@/hooks/useBatchJob';
import { isTerminalBatchJobStatus, useBatchJob } from '@/hooks/useBatchJob';
import { capitalizeFirstLetter } from '@/utils/strings';

const PERCENT_MULTIPLIER = 100;
const MAX_PERCENT = 100;
const STORAGE_KEY = 'stratumn.batchProgressToast';
// Fired whenever the persisted entry changes (set/cleared), so other UI (the
// toolbar "last batch" button) can react without prop-drilling — same
// window-CustomEvent idiom the overview context uses for saved views.
const CHANGE_EVENT = 'batchProgressToastChange';

// Persisted so a batch toast (especially a COMPLETED one) survives a reload
// until the user explicitly dismisses it. Scoped by workflow so a toast only
// re-appears on the workflow it belongs to.
type PersistedToast = {
  jobId: string;
  workflowId: string;
};

function emitChange(): void {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function persist(entry: PersistedToast): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage unavailable (private mode / quota) — toast just won't persist.
  }
  emitChange();
}

export function readPersistedToast(): PersistedToast | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.jobId === 'string' &&
      typeof parsed?.workflowId === 'string'
    ) {
      return {
        jobId: parsed.jobId,
        workflowId: parsed.workflowId
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Reactive read of the persisted "last batch" entry for a given workflow, or
 * null. Backs the toolbar "last batch" button: it updates live as the toast is
 * created/dismissed (CHANGE_EVENT) and re-reads on mount (survives reload).
 * Scoped by workflow so the button only shows on the workflow the batch ran on.
 */
export function useLastBatchToast(workflowId: string): PersistedToast | null {
  const read = useCallback(() => {
    const entry = readPersistedToast();
    return entry && entry.workflowId === workflowId ? entry : null;
  }, [workflowId]);

  const [entry, setEntry] = useState<PersistedToast | null>(read);

  useEffect(() => {
    setEntry(read());
    const handler = () => setEntry(read());
    window.addEventListener(CHANGE_EVENT, handler);
    // Cross-tab: storage events fire on other tabs when localStorage changes.
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, [read]);

  return entry;
}

function toastIdFor(jobId: string): string {
  return `batch-progress-${jobId}`;
}

function percentOf(job: BatchJob): number {
  if (job.total === 0) {
    return 0;
  }
  return Math.min(
    MAX_PERCENT,
    Math.round((job.processed / job.total) * PERCENT_MULTIPLIER)
  );
}

function iconFor(terminal: boolean, failed: boolean) {
  if (!terminal) {
    return <Loader2 className="animate-spin" size={16} />;
  }
  if (failed) {
    return <XIcon size={16} />;
  }
  return <Check size={16} />;
}

type CardProps = {
  job: BatchJob | null;
  onOpen: () => void;
};

export function BatchProgressToastCard({ job, onOpen }: CardProps) {
  const terminal = job ? isTerminalBatchJobStatus(job.status) : false;
  const failed = (job?.failed ?? 0) > 0;
  const percent = job ? percentOf(job) : 0;
  const title = job ? (job.actionTitle ?? job.actionKey) : null;
  const groupLabel = job?.groupLabel;

  return (
    <button
      className="flex w-full items-center gap-3 text-left"
      data-testid="batch-progress-toast"
      onClick={onOpen}
      type="button"
    >
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full',
          !terminal && 'bg-primary/15 text-primary',
          terminal && failed && 'bg-destructive/15 text-destructive',
          terminal && !failed && 'bg-green-600/15 text-green-600'
        )}
      >
        {iconFor(terminal, failed)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm">
          Batch action
          {title ? (
            <>
              {' · '}
              <span className="font-semibold first-letter:uppercase">
                {title}
              </span>
            </>
          ) : null}
          {groupLabel ? (
            <>
              {' · '}
              <span className="text-foreground/60 italic">
                {capitalizeFirstLetter(groupLabel)}
              </span>
            </>
          ) : null}
        </span>
        {renderBody(job, terminal, failed, percent)}
      </span>
    </button>
  );
}

function renderBody(
  job: BatchJob | null,
  terminal: boolean,
  failed: boolean,
  percent: number
) {
  if (!job) {
    return <span className="block text-foreground/60 text-xs">Loading…</span>;
  }
  if (terminal) {
    return (
      <span className="block text-foreground/60 text-xs tabular-nums">
        {failed
          ? `Completed · ${job.succeeded} ok / ${job.failed} failed`
          : `Completed · ${job.succeeded} ok`}
      </span>
    );
  }
  return (
    <span className="mt-1 block">
      <span className="mb-1 flex items-center justify-between text-foreground/60 text-xs tabular-nums">
        <span>{`${job.processed} / ${job.total}`}</span>
        <span>{`${percent}%`}</span>
      </span>
      <span className="block h-1 w-full overflow-hidden rounded-full bg-foreground/10">
        <span
          className="block h-full rounded-full bg-primary transition-all"
          data-testid="batch-toast-bar"
          style={{ width: `${percent}%` }}
        />
      </span>
    </span>
  );
}

// How long a COMPLETED toast lingers before auto-dismissing (matches the app's
// standard transient toast feel). Only starts once the job is terminal.
const TERMINAL_AUTODISMISS_MS = 5000;

type ToastProps = {
  jobId: string;
  onOpen: () => void;
  onClose: () => void;
};

export function BatchProgressToast({ jobId, onOpen, onClose }: ToastProps) {
  const { job, loading } = useBatchJob(jobId);
  const terminal = job ? isTerminalBatchJobStatus(job.status) : false;

  useEffect(() => {
    if (!loading && job === null) {
      onClose();
      return;
    }
    if (!terminal) {
      return;
    }
    const timer = setTimeout(onClose, TERMINAL_AUTODISMISS_MS);
    return () => clearTimeout(timer);
  }, [loading, job, terminal, onClose]);

  return <BatchProgressToastCard job={job} onOpen={onOpen} />;
}

export function showBatchProgressToast(
  args: {
    jobId: string;
    workflowId: string;
  },
  onOpen: () => void
): void {
  const { jobId, workflowId } = args;
  persist({ jobId, workflowId });
  const id = toastIdFor(jobId);
  // Dismisses the toast UI but NEVER clears the persisted entry — the toolbar "last batch" button depends on it.
  const close = () => notify.dismiss(id);
  notify(<BatchProgressToast jobId={jobId} onClose={close} onOpen={onOpen} />, {
    id,
    duration: Number.POSITIVE_INFINITY
  });
}
